import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HardHat, User, Check, ChevronUp, UserX, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function WorkshopUstaSwitcher() {
  const { user, selectUsta, clearUsta } = useAuth();
  const [open, setOpen] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  const isWorkshop = !!user && (user.role === "atolye" || user.is_impersonated);
  const activeUstaId = user?.is_impersonated ? user.id : null;
  const activeName = user?.is_impersonated ? user.name : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/workers");
      setWorkers(data);
    } catch {
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isWorkshop) load();
  }, [isWorkshop, load]);

  if (!isWorkshop) return null;

  const pick = async (w) => {
    if (switching) return;
    setSwitching(true);
    try {
      await selectUsta(w.id);
      setOpen(false);
      // Reload the current page so all data reflects the newly selected usta
      // without the user having to switch tabs manually.
      window.location.reload();
    } catch {
      toast.error("Usta seçilemedi");
      setSwitching(false);
    }
  };

  const release = async () => {
    if (switching) return;
    setSwitching(true);
    try {
      await clearUsta();
      setOpen(false);
      window.location.reload();
    } catch {
      toast.error("İşlem başarısız");
      setSwitching(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3" data-testid="workshop-switcher">
      {/* Expanding panel */}
      <div
        className={`origin-bottom-right transition-all duration-200 ease-out ${
          open ? "opacity-100 translate-y-0 scale-100 pointer-events-auto" : "opacity-0 translate-y-2 scale-95 pointer-events-none"
        }`}
      >
        <div className="w-72 max-h-[70vh] overflow-y-auto rounded-2xl border border-zinc-200 bg-white shadow-2xl" data-testid="workshop-panel">
          <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/70 rounded-t-2xl">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Atölye Tableti</div>
            <div className="font-semibold text-zinc-800 mt-0.5">Kim çalışıyor?</div>
          </div>

          <div className="p-2">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500 px-3 py-4">
                <Loader2 size={14} className="animate-spin" /> Yükleniyor...
              </div>
            ) : workers.length === 0 ? (
              <div className="text-sm text-zinc-500 px-3 py-4 text-center">Kayıtlı usta bulunamadı.</div>
            ) : (
              <ul className="space-y-1">
                {workers.map((w) => {
                  const active = w.id === activeUstaId;
                  return (
                    <li key={w.id}>
                      <button
                        type="button"
                        onClick={() => pick(w)}
                        disabled={switching}
                        data-testid={`workshop-usta-${w.id}`}
                        className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                          active
                            ? "bg-amber-50 border border-amber-200"
                            : "hover:bg-zinc-50 border border-transparent"
                        }`}
                      >
                        <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${active ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-500"}`}>
                          <User size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-medium text-zinc-800 truncate">{w.name}</span>
                          <span className="block text-xs text-zinc-500 truncate">{w.role}</span>
                        </span>
                        {active && <Check size={16} className="text-amber-600 shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {activeUstaId && (
              <button
                type="button"
                onClick={release}
                disabled={switching}
                data-testid="workshop-release"
                className="mt-2 w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm text-zinc-600 hover:bg-red-50 hover:text-red-600 transition-colors border border-transparent"
              >
                <UserX size={16} className="shrink-0" /> Seçimi bırak
              </button>
            )}
          </div>
        </div>
      </div>

      {/* The always-visible pill button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="workshop-switcher-button"
        className={`group flex items-center gap-2.5 rounded-full pl-3 pr-4 py-3 shadow-xl transition-all duration-200 active:scale-95 ${
          activeName ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-zinc-900 hover:bg-zinc-800 text-white"
        }`}
      >
        <span className="shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <HardHat size={18} />
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[10px] uppercase tracking-widest opacity-80">
            {activeName ? "Aktif Usta" : "Atölye"}
          </span>
          <span className="text-sm font-semibold max-w-[160px] truncate" data-testid="workshop-active-name">
            {activeName || "Usta Seçin"}
          </span>
        </span>
        <ChevronUp size={16} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
    </div>
  );
}
